/** Аккаунт: кто я, что заказывал, что мне причитается.
 *
 * Раньше у человека не было в приложении ни одного места про него самого:
 * каталог, игры и заявка, уходящая в никуда. Здесь появляется точка, к которой
 * привязывается всё личное — история, награды, приглашённые друзья.
 */
import { useEffect, useState } from 'react';
import { request } from '@/shared/api';
import type { AccountAnswer, CoinBurn, MyOrder, ReferralAnswer, Reward } from '@/shared/types';
import { Button, Note, Skeleton } from '@/shared/ui';
import { openManager, tap, tg } from '@/shared/tg';
import { count } from '@/shared/plural';
import {
  BoltGlyph, ChatGlyph, ChevronGlyph, ClockGlyph, FlameGlyph, GiftGlyph, ShareGlyph,
} from './icons';
import { CoinIcon } from '@/shared/coin';
import { Balance } from './market';
import { renderText } from '@/shared/richtext';
import { REWARD_TONE, isLive } from '@/shared/rewards';
import type { Screen } from './nav';

/** Дата коротко: «с 25 июля». */
function since(stamp: string | null): string {
  if (!stamp) return '—';
  const at = new Date(stamp);
  if (Number.isNaN(at.getTime())) return '—';
  return at.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function moment(stamp: string | null): string {
  if (!stamp) return '';
  const at = new Date(stamp);
  if (Number.isNaN(at.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(at.getDate())}.${pad(at.getMonth() + 1)}.${at.getFullYear()}`;
}

const STATUS: Record<string, { label: string; tone: string }> = {
  new: { label: 'ждёт подтверждения', tone: 'text-hint' },
  working: { label: 'в работе', tone: 'text-mint' },
  done: { label: 'выполнен', tone: 'text-good' },
  cancelled: { label: 'отменён', tone: 'text-danger' },
};

/** Награда карточкой — одинаково и в аккаунте, и при выборе на заказ.
 *
 * Главная строка под названием — состояние, а не источник. Раньше там писали
 * «начислена», и человек читал это как «уже выдали»: источник объясняет
 * прошлое, состояние обещает будущее, и путать их нельзя. Откуда награда
 * взялась, тоже видно — но следом и мельче.
 */
export function RewardCard({ reward, right }: { reward: Reward; right?: React.ReactNode }) {
  const percent = reward.kind === 'percent';
  const tone = REWARD_TONE[reward.state];
  return (
    <div
      className={[
        'glass wet-edge flex items-center gap-3 rounded-card px-3.5 py-3',
        reward.state === 'revoked' ? 'opacity-55' : '',
      ].join(' ')}
    >
      <span
        className={[
          'grid size-10 shrink-0 place-items-center rounded-xl text-sm font-[720]',
          tone.fill,
          tone.ink,
        ].join(' ')}
      >
        {percent ? `−${Math.round(reward.value ?? 0)}%` : '🎁'}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-[650]">{reward.title}</span>
        <span className="mt-0.5 block truncate text-xs">
          <span className={`font-[620] ${tone.ink}`}>{reward.state_label}</span>
          <span className="text-hint"> · {reward.source_label}</span>
        </span>
      </span>
      {right}
    </div>
  );
}

/* ------------------------------------------------------------- аккаунт */

export function AccountScreen({
  onOpen,
  managerUrl,
}: {
  onOpen: (screen: Screen) => void;
  managerUrl: string;
}) {
  const [data, setData] = useState<AccountAnswer | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    request<AccountAnswer>('/api/account', {})
      .then(setData)
      .catch((error: Error) => setFailure(error.message));
  }, []);

  if (failure) return <Note tone="warn">Не открылось: {failure}</Note>;
  if (!data) return <Skeleton className="h-40" />;

  return (
    <>
      <div className="glass wet-edge screen-in flex items-center gap-3.5 rounded-layer p-4">
        {/* Кабинет начинается с человека: круг с его буквой, а не безликая
            плашка. Кольцо — мята к золоту, два наших смысла в одном ободке */}
        <span className="avatar-ring grid size-14 shrink-0 place-items-center rounded-full">
          <span className="grid size-[52px] place-items-center rounded-full bg-night-2 text-xl font-[760] text-mint">
            {(data.name || '?').trim().charAt(0).toUpperCase()}
          </span>
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-lg font-[700] tracking-[-.02em]">{data.name}</span>
          {data.username && (
            <span className="block truncate text-sm text-hint">@{data.username}</span>
          )}
          <span className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[12px] text-hint">
            <span data-numeric>{count(data.orders, 'заказ', 'заказа', 'заказов')}</span>
            <span className="size-1 rounded-full bg-glass-line" aria-hidden />
            <span>с нами {since(data.since)}</span>
          </span>
        </span>
      </div>

      <Balance amount={data.balance} onMarket={() => onOpen({ type: 'market', title: 'Магазин бонусов' })} />
      {data.burn && <BurnSoon burn={data.burn} />}

      <Rewards live={data.rewards} history={data.reward_history ?? []} />

      {/* Переходы, а не действия: ряд с иконкой и шевроном честнее кнопки —
          он обещает следующий экран, а не поступок */}
      <h2 className="mt-6 mb-2 text-lg font-[680] tracking-[-.02em]">Ещё</h2>
      <div className="grid gap-2">
        <AccountRow
          icon={<ClockGlyph size={19} />}
          title="История заказов"
          note="Составы, суммы и статусы"
          onOpen={() => onOpen({ type: 'my-orders', title: 'Мои заказы' })}
        />
        {data.referral_enabled && (
          <AccountRow
            icon={<ShareGlyph size={19} />}
            title="Приглашайте друзей"
            note="Паркоины за каждого, кто закажет"
            onOpen={() => onOpen({ type: 'referral', title: 'Приглашайте друзей' })}
          />
        )}
        <AccountRow
          icon={<ChatGlyph size={19} />}
          title="Написать менеджеру"
          note="Вопрос по заказу, доставка, опт"
          onOpen={() => openManager(managerUrl)}
        />
      </div>
    </>
  );
}

/** Ряд-переход кабинета: круг с иконкой, название, поясняющая строка. */
function AccountRow({
  icon,
  title,
  note,
  onOpen,
}: {
  icon: React.ReactNode;
  title: string;
  note: string;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        tap();
        onOpen();
      }}
      className="glass wet-edge flex w-full items-center gap-3.5 rounded-card px-3.5 py-3 text-left transition-[transform,border-color] duration-150 ease-glass active:scale-[.985] active:border-mint"
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-full bg-glass-2 text-mint">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14.5px] font-[650]">{title}</span>
        <span className="block truncate text-[12.5px] text-hint">{note}</span>
      </span>
      <span className="shrink-0 text-hint">
        <ChevronGlyph size={16} />
      </span>
    </button>
  );
}

/* ------------------------------------------------------ история заказов */

export function MyOrdersScreen() {
  const [orders, setOrders] = useState<MyOrder[] | null>(null);

  useEffect(() => {
    request<{ orders: MyOrder[] }>('/api/account/orders', {})
      .then((answer) => setOrders(answer.orders))
      .catch(() => setOrders([]));
  }, []);

  if (!orders) return <Skeleton className="h-40" />;
  if (!orders.length) return <Note>Заказов ещё не было.</Note>;

  return (
    <div className="grid gap-2.5">
      {orders.map((order, index) => {
        const status = STATUS[order.status] ?? STATUS.new!;
        return (
          <div
            key={order.id}
            style={{ animationDelay: `${Math.min(index, 12) * 28}ms` }}
            className="glass wet-edge animate-rise rounded-card px-3.5 py-3"
          >
            <div className="flex items-baseline gap-2">
              <span className="min-w-0 flex-1 truncate text-[14px] font-[680]">
                Заказ №{order.id}
              </span>
              <span className={`shrink-0 text-[11px] font-[650] ${status.tone}`}>
                {status.label}
              </span>
            </div>

            {/* Состав построчно: корзинный заказ из трёх позиций — одна
                карточка, а не три одинаковых подряд */}
            <div className="mt-1.5 grid gap-1">
              {order.items.map((item, at) => (
                <div key={at} className="flex items-baseline gap-2 text-[13.5px]">
                  <span className="min-w-0 flex-1 leading-tight break-words">
                    {item.title}
                    {item.variants && (
                      <span className="text-hint"> — {item.variants}</span>
                    )}
                    {item.qty > 1 && (
                      <span className="font-[680] text-hint" data-numeric> ×{item.qty}</span>
                    )}
                  </span>
                  <span className="shrink-0 text-xs text-hint" data-numeric>
                    {item.price && `${item.price} BYN`}
                  </span>
                </div>
              ))}
              {order.express && (
                <div className="flex items-baseline gap-2 text-[13.5px]">
                  <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate">
                    <BoltGlyph size={14} className="shrink-0 text-mint" />
                    {order.express.title}
                  </span>
                  <span className="shrink-0 text-xs text-hint" data-numeric>
                    {order.express.price} BYN
                  </span>
                </div>
              )}
              {/* Применённые бонусы. Подарочный экспресс не дублируем: он уже
                  стоит строкой доставки с подписью «подарок» */}
              {order.rewards
                ?.filter((reward) => reward.kind !== 'express')
                .map((reward, at) => (
                  <div key={`b${at}`} className="flex items-baseline gap-2 text-[13.5px]">
                    <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate text-gold-2">
                      {reward.kind === 'percent' ? (
                        <span className="grid size-[14px] shrink-0 place-items-center text-[11px] font-[800]">
                          %
                        </span>
                      ) : (
                        <GiftGlyph size={14} className="shrink-0" />
                      )}
                      {reward.title}
                    </span>
                    <span className="shrink-0 text-xs text-gold-2/70">бонус</span>
                  </div>
                ))}
            </div>

            <div className="mt-2 flex items-baseline gap-2 border-t border-glass-line pt-2">
              <span className="min-w-0 flex-1 truncate text-[11px] text-hint/60">
                {moment(order.at)}
                {order.address && ` · ${order.address}`}
              </span>
              {order.total !== null && (
                <span className="shrink-0 text-[14px] font-[720]" data-numeric>
                  {order.total} BYN
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* --------------------------------------------------------- приглашения */

export function ReferralScreen() {
  const [data, setData] = useState<ReferralAnswer | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    request<ReferralAnswer>('/api/referral', {})
      .then(setData)
      .catch(() => setData({ enabled: false }));
  }, []);

  if (!data) return <Skeleton className="h-40" />;
  if (!data.enabled) return <Note>Программа сейчас не работает.</Note>;

  const progress = data.progress;
  const share = () => {
    tap();
    const url = `https://t.me/share/url?url=${encodeURIComponent(data.link ?? '')}` +
      `&text=${encodeURIComponent('Заходи, тут дают паркоины на первый заказ')}`;
    const app = tg();
    try {
      if (app?.openTelegramLink) {
        app.openTelegramLink(url);
        return;
      }
    } catch {
      /* старый клиент — уходим на обычное открытие */
    }
    window.open(url, '_blank');
  };

  const copy = async () => {
    tap();
    try {
      await navigator.clipboard.writeText(data.link ?? '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* буфер недоступен — остаётся кнопка «поделиться» */
    }
  };

  return (
    <>
      {data.text && <div className="screen-in mb-3 text-[15px] leading-relaxed">{renderText(data.text)}</div>}

      <div className="glass wet-edge rounded-layer p-4">
        <div className="mb-2 text-xs text-hint">Ваша ссылка</div>
        <div className="rounded-xl bg-glass px-3 py-2.5 font-mono text-[13px] break-all text-frost">
          {data.link}
        </div>
        <div className="mt-2.5 grid grid-cols-2 gap-2">
          <Button onClick={share}>Поделиться</Button>
          <Button tone="ghost" onClick={copy}>
            {copied ? 'Скопировано' : 'Скопировать'}
          </Button>
        </div>
      </div>

      {progress && progress.tiers.length > 0 && <Progress progress={progress} />}

      <h2 className="mt-5 mb-2 text-lg font-[680] tracking-[-.02em]">Ваши друзья</h2>
      {data.invited && data.invited.length ? (
        <div className="grid gap-2">
          {data.invited.map((friend, index) => (
            <div
              key={friend.user_id}
              style={{ animationDelay: `${Math.min(index, 12) * 28}ms` }}
              className="glass animate-rise flex items-center gap-3 rounded-card px-3.5 py-3"
            >
              <span
                className={[
                  'size-2 shrink-0 rounded-full',
                  friend.credited ? 'bg-good' : 'bg-hint/40',
                ].join(' ')}
              />
              <span className="min-w-0 flex-1 truncate text-[15px]">
                {friend.username ? `@${friend.username}` : friend.name || 'Друг'}
              </span>
              <span className={`shrink-0 text-xs ${friend.credited ? 'text-good' : 'text-hint'}`}>
                {friend.credited ? 'засчитан' : 'ждёт заказа'}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <Note>Пока никого. Отправьте ссылку другу — паркоины упадут обоим.</Note>
      )}
    </>
  );
}

/** Награды за друзей — один путь с остановками.
 *
 * Сперва были кружочки: они отвечали только на «сколько осталось до
 * ближайшего». Потом лента карточек: она показала все награды, но развалила
 * дорогу на отдельные плитки, между которыми нет связи, — а связь и есть
 * главное. Пять друзей это не отдельная задача, это первая остановка на пути,
 * который продолжается дальше.
 *
 * Поэтому дорожка. Пройденное залито, ближайшая остановка подсвечена, дальние
 * приглушены — и видно не только «сколько осталось», но и ради чего идти
 * дальше. Отрезки между остановками равны по высоте, а заливка внутри отрезка
 * идёт по своим друзьям: путь от одного подарка к другому одинаково длинный
 * на вид, каким бы ни был шаг в людях.
 *
 * Первой стоит не награда, а начало пути — со счётчиком засчитанных. Без него
 * дорожка начиналась прямо с первой награды, то есть утверждала, что ноль
 * друзей это уже первый подарок; заливка при этом врала на целый отрезок.
 */
function Progress({
  progress,
}: {
  progress: NonNullable<ReferralAnswer['progress']>;
}) {
  const tiers = progress.tiers;
  if (!tiers.length) return null;

  const done = tiers.filter((tier) => tier.done).length;
  // Сколько пройдено внутри текущего отрезка: от прошлой остановки к следующей
  const from = done > 0 ? (tiers[done - 1]?.people ?? 0) : 0;
  const to = tiers[done]?.people ?? from;
  const within = to > from ? Math.min(1, Math.max(0, (progress.count - from) / (to - from))) : 0;
  // Отрезков ровно столько, сколько наград: от начала пути до первой, от неё
  // до второй и так далее
  const filled = Math.min(1, (done + within) / tiers.length);

  return (
    <div className="mt-4">
      <h2 className="mb-2.5 text-lg font-[680] tracking-[-.02em]">Паркоины за друзей</h2>

      <div className="tier-path">
        {/* Дорожка целиком и пройденная её часть. Заливка тянется к своей
            высоте плавно — видно, что путь один, а не набор ступенек */}
        <span className="tier-rail" aria-hidden>
          <span
            className="tier-fill"
            style={{ clipPath: `inset(0 0 ${100 - Math.round(filled * 100)}% 0)` }}
          />
        </span>

        {/* Начало пути: отсюда считается заливка, и здесь же видно, сколько
            друзей уже засчитано */}
        <div className="relative flex gap-3 pb-5">
          <span className="tier-stop start" aria-hidden>
            •
          </span>
          <span className="min-w-0 flex-1 pt-1">
            <span className="text-[15px] leading-snug font-[650]">
              {count(progress.count, 'друг', 'друга', 'друзей')} засчитано
            </span>
            <span className="mt-0.5 block text-[11px] text-hint">
              приглашено {progress.invited}
            </span>
          </span>
        </div>

        {tiers.map((tier, index) => {
          const next = !tier.done && index === done;
          const left = Math.max(0, tier.people - progress.count);
          return (
            <div
              key={tier.people}
              style={{ animationDelay: `${index * 60}ms` }}
              className="animate-rise relative flex gap-3 pb-5 last:pb-0"
            >
              <span
                className={[
                  'tier-stop',
                  tier.done ? 'done' : next ? 'next' : '',
                ].join(' ')}
                aria-hidden
              >
                {tier.done ? '✓' : <CoinIcon size={15} />}
              </span>

              <span className={`min-w-0 flex-1 pt-0.5 ${tier.done || next ? '' : 'opacity-60'}`}>
                <span className="flex items-baseline gap-2">
                  <span className="text-[15px] leading-snug font-[650]" data-numeric>
                    +{count(tier.coins, 'паркоин', 'паркоина', 'паркоинов')}
                  </span>
                  <span className="ml-auto shrink-0 text-[11px] text-hint" data-numeric>
                    {count(tier.people, 'друг', 'друга', 'друзей')}
                  </span>
                </span>
                <span
                  className={[
                    'mt-0.5 block text-[11px]',
                    tier.done ? 'text-gold-2' : next ? 'text-gold-2' : 'text-hint',
                  ].join(' ')}
                >
                  {tier.done
                    ? 'начислено — уже в вашем балансе'
                    : next
                      ? `осталось ${count(left, 'друг', 'друга', 'друзей')}`
                      : `ещё ${count(left, 'друг', 'друга', 'друзей')}`}
                </span>
              </span>
            </div>
          );
        })}
      </div>

      <Note>Друг засчитывается, когда его заказ подтвердит менеджер.</Note>
    </div>
  );
}

/* ------------------------------------------------------------- награды */

/** Что у меня есть сейчас и что было раньше.
 *
 * Два вопроса, и оба настоящие. Первый — «чем я могу воспользоваться»;
 * второй — «куда делась скидка, которую я видел на прошлой неделе». Пока был
 * один список без состояний, ни на один ответа не было: подарок, скидка и
 * отменённое лежали вперемешку и выглядели одинаково.
 */
const DAY_MS = 86_400_000;

/** Ближайшее сгорание: сколько и когда — и что его отодвигает.
 *
 * Сгорание не должно приходить сюрпризом: человек видит его рядом с
 * балансом заранее. За три дня до срока строка теплеет — пора заказывать.
 */
function BurnSoon({ burn }: { burn: CoinBurn }) {
  const left = new Date(burn.at).getTime() - Date.now();
  const days = Math.max(1, Math.ceil(left / DAY_MS));
  const soon = left < 3 * DAY_MS;
  const when = left < DAY_MS ? 'В ближайшие сутки' : `Через ${count(days, 'день', 'дня', 'дней')}`;
  const every = burn.days === 1 ? 'Каждый день' : `Каждые ${count(burn.days, 'день', 'дня', 'дней')}`;
  return (
    <div
      className={[
        'screen-in mt-2 flex items-start gap-2.5 rounded-card px-3.5 py-2.5',
        soon ? 'border border-gold/35 bg-gold/10' : 'glass',
      ].join(' ')}
    >
      <FlameGlyph size={18} className={`mt-0.5 shrink-0 ${soon ? 'text-gold-2' : 'text-hint'}`} />
      <span className="min-w-0 flex-1">
        <span
          className={`block text-[13px] leading-snug font-[680] ${soon ? 'text-gold-2' : 'text-frost'}`}
          data-numeric
        >
          {when} сгорит {count(burn.coins, 'паркоин', 'паркоина', 'паркоинов')}
        </span>
        <span className="mt-0.5 block text-[12px] leading-snug text-hint" data-numeric>
          {every} без заказа сгорает {burn.percent}% баланса. Сделайте заказ — и срок
          сдвинется.
        </span>
      </span>
    </div>
  );
}

function Rewards({ live, history }: { live: Reward[]; history: Reward[] }) {
  const [all, setAll] = useState(false);
  const [opened, setOpened] = useState(false);
  // В истории то, что уже отыграло: применённое, полученное, отменённое
  const past = history.filter((reward) => !isLive(reward));
  const gifts = live.filter((reward) => reward.kind !== 'percent');
  const percents = live.filter((reward) => reward.kind === 'percent');

  return (
    <>
      <h2 className="mt-5 mb-2 text-lg font-[680] tracking-[-.02em]">Мои бонусы</h2>

      {live.length ? (
        <>
          <div className="grid gap-2">
            {(opened ? live : live.slice(0, 3)).map((reward, index) => (
              <div
                key={reward.id}
                style={{ animationDelay: `${index * 28}ms` }}
                className="animate-rise"
              >
                <RewardCard reward={reward} />
              </div>
            ))}
          </div>
          {live.length > 3 && (
            /* Больше трёх — прячем: кабинет про «что у меня есть», а не про
               прокрутку. Кнопка называет, сколько ещё лежит под сгибом */
            <button
              type="button"
              onClick={() => {
                tap();
                setOpened((was) => !was);
              }}
              className="glass mt-2 w-full rounded-card px-3.5 py-2.5 text-[13px] font-[620] text-link transition-colors duration-150 active:bg-glass-active"
            >
              {opened ? 'Свернуть' : `Показать все (${live.length})`}
            </button>
          )}
          <Note>
            {percents.length > 0 && (
              <>
                Скидки не суммируются: к заказу применяется одна — выберите её
                при оформлении.
              </>
            )}
            {gifts.length > 0 && (
              <>
                {percents.length > 0 && <br />}
                {count(gifts.length, 'подарок', 'подарка', 'подарков')} ждёт выдачи:
                приложите к заказу или напишите менеджеру — он вынесет.
              </>
            )}
          </Note>
        </>
      ) : (
        <Note>Сейчас наград нет. Они приходят за игры и за приглашённых друзей.</Note>
      )}

      {past.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => {
              tap();
              setAll((was) => !was);
            }}
            className="glass mt-3 w-full rounded-card px-4 py-3 text-sm font-[620] text-hint transition-colors duration-150 active:bg-glass-active"
          >
            {all ? 'Скрыть историю' : `История наград (${past.length})`}
          </button>

          {all && (
            <div className="animate-fade mt-2 grid gap-2">
              {past.map((reward) => (
                <RewardCard key={reward.id} reward={reward} />
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}
